import { useRef, useState } from "react"
import PropTypes from "prop-types"
import { get, map, some, size, tail, isNull } from "lodash"
import classNames from "classnames"
import { IconButton } from "@mui/material"
import { Add, Remove } from "@mui/icons-material"
import CompareSelect from "components/material/compare-select"
import { colorWithOpacity } from "helpers/colors"
import useResizeObserver from "hooks/resize-observer"
import Tooltip from "components/material/tooltip"
import { TOP_COMPOSITION_FACTORS } from "helpers/analyzeConstants"
import TitleRow from "./components/title-row"
import Column from "./components/column"
import Loading from "components/loading"
import { getToggleAllTitle } from "components/factor-categories/helpers"
import { Panel, GroupSelectArea, GroupSelectorBox, ShowAllToggleBox, ContentArea, ValuesBox } from "./styled"

function getBackgroundColor(loading, color) {
  return {
    background: loading
      ? `linear-gradient(90deg, ${colorWithOpacity(color, 0.1)}, ${colorWithOpacity(color, 0.3)})`
      : colorWithOpacity(color, 0.1),
  }
}

export default function CompareTableGroupsPanel({
  columns,
  items,
  data,
  groups,
  selectedGroup,
  setGroup,
  saveGroupToStore,
  storeName = "composition",
  topX = TOP_COMPOSITION_FACTORS,
  maxNbColumns = 5,
}) {
  const panelRef = useRef()
  const [showAll, setShowAll] = useState(false)
  const [width] = useResizeObserver(panelRef)

  const loading = some(data, datum => !isNull(datum) && get(datum, "loading") !== "ok")
  // -1 because items contains a total row
  const moreThanTopX = some(items, item => size(item) - 1 > topX)
  const scrollable = !showAll && moreThanTopX
  // 80 is the width required by the value fragment to display its widest value 100.00%
  const adjustedWidth = width / (maxNbColumns + 1) - 80

  return (
    <Panel ref={panelRef} className={classNames("components_compare-groups-panel fixed", { scrollable })}>
      <GroupSelectArea className={classNames("group-column", "group-select")}>
        <GroupSelectorBox className="group-selector">
          <CompareSelect
            options={groups}
            selectedOption={selectedGroup}
            setSelected={setGroup}
            saveToStore={saveGroupToStore}
            storeName={storeName}
          />
        </GroupSelectorBox>
        <ShowAllToggleBox className="show-all-toggle">
          <Tooltip title={getToggleAllTitle(showAll, moreThanTopX, selectedGroup.label)}>
            <span>
              <IconButton size="small" disabled={!moreThanTopX} onClick={() => setShowAll(!showAll)}>
                {!showAll ? <Add /> : <Remove />}
              </IconButton>
            </span>
          </Tooltip>
        </ShowAllToggleBox>
      </GroupSelectArea>
      <ContentArea className="content">
        {loading ? <Loading />
        : <>
          <TitleRow
            {...{
              columns,
              data,
              items,
              cellWidth: adjustedWidth,
              getBackgroundColor,
            }}
          />
          <ValuesBox
            className={classNames("values", { "show-all": showAll })}
            sx={{
              ...(scrollable && { flexFlow: "row wrap", overflow: "hidden auto" }),
              ...(showAll && { maxHeight: "none" }),
            }}
          >
            {map(columns, (column, i) => <Column
              key={`col${i}`}
              {...{
                id: `col${i}`,
                data: get(data, i),
                items: tail(get(items, i)),
                column,
                cellWidth: adjustedWidth,
                getBackgroundColor,
                scrollable,
                isLastColumn: i === 4,
              }}
            />,
            )}
          </ValuesBox>
        </>}
      </ContentArea>
    </Panel>
  )
}

CompareTableGroupsPanel.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    noData: PropTypes.bool,
    isUnmodeled: PropTypes.bool,
  })).isRequired,
  items: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  groups: PropTypes.array.isRequired,
  selectedGroup: PropTypes.object.isRequired,
  setGroup: PropTypes.func.isRequired,
  saveGroupToStore: PropTypes.func.isRequired,
  storeName: PropTypes.string,
  fixedItemNumber: PropTypes.bool,
  topX: PropTypes.number,
  adjustWidth: PropTypes.func,
  maxNbColumns: PropTypes.number,
}
